import { Button } from "@secure-vault/ui";
import {
  downloadVaultFile,
  getUnlockStatus,
  listUnlockPartials,
  listVaultFiles,
} from "@secure-vault/api";
import { decryptFilePayload, decryptWithKey } from "@/lib/crypto/masterKey";
import { aggregateAndUnwrap } from "@/lib/crypto/threshold";
import {
  CheckCircle2,
  Download,
  FileCheck,
  Loader2,
  RefreshCw,
} from "lucide-react";
import { FileListItem } from "@secure-vault/api/types";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

interface ExecutorAggregateContainerProps {
  sessionId: string;
  clientId: string;
}

/**
 * Executor aggregation panel (Phase 3D of NEW_VAULT.md). The Executor is the lead trustee
 * whose browser collects the blind-routed partials, reconstructs the master key via
 * Lagrange-in-the-exponent, and downloads + decrypts the vault files locally. The platform
 * only ever routes opaque partial points; the master key exists solely in this browser.
 */
export function ExecutorAggregateContainer({
  sessionId,
  clientId,
}: ExecutorAggregateContainerProps) {
  const [collected, setCollected] = useState(0);
  const [threshold, setThreshold] = useState(0);
  const [ready, setReady] = useState(false);
  const [aggregating, setAggregating] = useState(false);
  const [masterKey, setMasterKey] = useState<Uint8Array | null>(null);
  const [files, setFiles] = useState<FileListItem[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [decryptingFileId, setDecryptingFileId] = useState<string | null>(null);

  const refreshStatus = useCallback(async () => {
    try {
      const status = await getUnlockStatus(sessionId);
      setCollected(status.collected);
      setThreshold(status.threshold);
      setReady(status.collected >= status.threshold);
    } catch (err) {
      console.warn("Failed to poll unlock status:", err);
    }
  }, [sessionId]);

  // Poll the collection progress until the threshold is met.
  useEffect(() => {
    refreshStatus();
    const id = setInterval(() => {
      if (!masterKey) refreshStatus();
    }, 5000);
    return () => clearInterval(id);
  }, [refreshStatus, masterKey]);

  const handleAggregate = async () => {
    setAggregating(true);
    try {
      toast.loading("Preparing the vault documents...", {
        id: "aggregate",
      });
      const { partials, c2 } = await listUnlockPartials(sessionId);
      const mk = await aggregateAndUnwrap(
        partials.map((p) => ({ index: p.share_index, partial: p.partial })),
        c2
      );
      setMasterKey(mk);
      toast.success("The vault documents are ready.", {
        id: "aggregate",
      });
      await loadFiles();
    } catch (err: unknown) {
      console.error(err);
      toast.error("We couldn't open the vault documents. Please try again.", {
        id: "aggregate",
      });
    } finally {
      setAggregating(false);
    }
  };

  const loadFiles = async () => {
    setLoadingFiles(true);
    try {
      const list = await listVaultFiles(clientId);
      setFiles(list);
    } catch (err: unknown) {
      console.error(err);
      toast.error("We couldn't load the documents. Please try again.");
    } finally {
      setLoadingFiles(false);
    }
  };

  const handleDownloadFile = async (file: FileListItem) => {
    if (!masterKey) return;
    setDecryptingFileId(file.file_id);
    try {
      toast.loading(`Preparing ${file.original_filename}...`, {
        id: "decrypt-file",
      });
      const fileKey = await decryptWithKey(file.encrypted_file_key, masterKey);
      const encryptedBlob = await downloadVaultFile(file.file_id);
      const decryptedBlob = await decryptFilePayload(
        encryptedBlob,
        fileKey,
        file.mime_type || undefined
      );

      const url = URL.createObjectURL(decryptedBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.original_filename || "recovered-file";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success("Document downloaded.", { id: "decrypt-file" });
    } catch (err: unknown) {
      console.error(err);
      toast.error("We couldn't download this document. Please try again.", {
        id: "decrypt-file",
      });
    } finally {
      setDecryptingFileId(null);
    }
  };

  return (
    <div className="bg-card border-border space-y-6 rounded-xl border p-6 shadow-sm">
      <div className="flex items-center gap-2.5 border-b pb-2">
        <FileCheck className="text-primary h-5 w-5" />
        <h2 className="text-base font-semibold">Executor access</h2>
      </div>

      <div className="flex items-center justify-between rounded-lg border p-3 text-xs">
        <span className="text-muted-foreground">
          Trustee approvals:{" "}
          <span className="text-foreground font-semibold">
            {collected} / {threshold || "?"}
          </span>
        </span>
        <Button
          variant="ghost"
          size="sm"
          onClick={refreshStatus}
          className="h-7 px-2 text-xs"
        >
          <RefreshCw className="mr-1 h-3.5 w-3.5" />
          Refresh
        </Button>
      </div>

      {!masterKey ? (
        <Button
          onClick={handleAggregate}
          disabled={!ready || aggregating}
          className="w-full text-xs"
        >
          {aggregating ? (
            <>
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              Opening vault...
            </>
          ) : ready ? (
            "Open vault documents"
          ) : !threshold ? (
            "Checking trustee approvals..."
          ) : (
            `Waiting for ${Math.max(threshold - collected, 1)} more trustee ${
              threshold - collected === 1 ? "approval" : "approvals"
            }`
          )}
        </Button>
      ) : (
        <div className="flex items-center gap-2 rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-3 text-xs text-emerald-500">
          <CheckCircle2 className="h-4 w-4" />
          The vault documents are ready.
        </div>
      )}

      {masterKey && (
        <div className="space-y-3">
          <h3 className="flex items-center gap-2 text-sm font-semibold">
            <FileCheck className="h-5 w-5 text-emerald-500" />
            Documents
          </h3>
          {loadingFiles ? (
            <div className="flex justify-center py-6">
              <Loader2 className="text-primary h-6 w-6 animate-spin" />
            </div>
          ) : files.length === 0 ? (
            <p className="text-muted-foreground text-xs">No documents found.</p>
          ) : (
            <div className="divide-border/60 divide-y">
              {files.map((file) => (
                <div
                  key={file.file_id}
                  className="flex items-center justify-between gap-3 py-3 text-xs"
                >
                  <span
                    className="text-foreground min-w-0 truncate font-medium"
                    title={file.original_filename || undefined}
                  >
                    {file.original_filename}
                  </span>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => handleDownloadFile(file)}
                    disabled={decryptingFileId === file.file_id}
                    className="text-primary hover:bg-primary/5 h-8 w-8"
                    title={`Download ${file.original_filename}`}
                  >
                    {decryptingFileId === file.file_id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
